<template>
  <div
    :id="id"
    class="element video draggable"
    :class="[
      cssClasses,
      animationInStyle,
      animationOutStyle,
      { selected: isSelected, locked }
    ]"
    :style="[basicStyles, transformStyles]"
  >
    <transition
      :enter-active-class="'animated ' + animationIn"
      :leave-active-class="'animated ' + animationOut"
      :duration="{ leave: leaveDuration }"
      mode=""
      @after-enter="afterEnter"
      @before-leave="beforeLeave"
    >
      <div
        v-show="toggle"
        class="video-wrap"
        :class="[loopingClass]"
        :style="[
          animationStyles,
          borderStyles,
          boxShadowStyles,
          blurStyles,
          basicStyles,
        ]"
      >
        <video
          ref="video"
          height="100%"
          width="100%"
          muted="true"
          :preload="isRenderMode ? 'metadata' : 'auto'"
          @timeupdate="handleTimeupdate"
          @loadeddata="handleLoadeddata"
          @ended="handleEnded"
        >
          <source
            :src="element.src"
            :type="element.contentType"
          />
        </video>
      </div>
    </transition>
  </div>
</template>

<script>
import { throttle } from 'lodash-es';
import EventBus from '../../utils/event-bus';
import ElementsMixins from '../../mixins/elements-mixins';
import MoveMixins from '../../mixins/move-mixins';
import ResizeMixins from '../../mixins/resize-mixins';

export default {
  name: 'VideoElement',
  mixins: [ElementsMixins, ResizeMixins, MoveMixins],

  inject: {
    isRenderMode: {
      default: false,
    },
  },

  computed: {
    isTrimmed() {
      return (
        this.element.videoOptions.start !== 0 || this.element.videoOptions.end
      );
    },

    contentType() {
      return this.element.contentType ? this.element.contentType : 'video/mp4';
    },
  },

  watch: {
    'element.src': {
      handler() {
        this.$refs.video.load();
      },
    },
  },

  mounted() {
    EventBus.on('play-pause-video', this.playPause);
    EventBus.on('mute-video', this.mute);
    EventBus.on('update-video-trim-options', this.previewTrim);
  },

  beforeUnmount() {
    EventBus.off('play-pause-video', this.playPause);
    EventBus.off('mute-video', this.mute);
    EventBus.off('update-video-trim-options', this.previewTrim);
  },

  methods: {
    handleResize: throttle(function onResize() {
      if (this.isSelected) {
        EventBus.emit('set-size', {
          height: this.$refs.video.clientHeight,
          width: this.$refs.video.clientWidth,
        });
      }
    }, 500),

    handleTimeupdate(e) {
      if (!this.element?.videoOptions?.playing) return;

      if (this.isTrimmed) {
        const { start, end, loop } = this.element.videoOptions;

        if (e.target.currentTime < start) {
          this.setCurrentTime(start);
        }
        if (end && e.target.currentTime > end) {
          this.pauseVideo();
          if (loop) {
            this.setCurrentTime(start);
            this.playVideo();
          }
        }
      }
    },

    handleLoadeddata() {
      this.pauseVideo();
      this.setCurrentTime(this.element.videoOptions.start);
      if (this.element.videoOptions.autoplay) {
        this.playVideo();
      }
    },

    handleEnded() {
      this.pauseVideo();
      if (this.element.videoOptions.loop) {
        this.setCurrentTime(this.element.videoOptions.start);
        this.playVideo();
      }
    },

    playPause() {
      if (this.isSelected) {
        const vid = this.$refs.video;
        if (vid.paused || vid.ended) {
          const { end, start } = this.element.videoOptions;
          if (end && vid.currentTime > end) {
            this.setCurrentTime(start);
          }
          this.playVideo();
        } else {
          this.pauseVideo();
        }
      }
    },

    async playVideo() {
      if (this.isRenderMode) { return; }

      // try/catch for chrome blocking autoplay sometimes so our playing state is accurate
      try {
        await this.$refs.video.play();

        this.updateVideoOptions({
          id: this.id,
          propKey: 'playing',
          propValue: true,
        }, {
          // blacklist this play button setting from undo/redo
          blacklist: true,
        });
      } catch (error) {
        this.updateVideoOptions({
          id: this.id,
          propKey: 'playing',
          propValue: false,
        }, {
          // blacklist this play button setting from undo/redo
          blacklist: true,
        });
      }
    },

    pauseVideo() {
      this.$refs.video.pause();
      this.updateVideoOptions({
        id: this.id,
        propKey: 'playing',
        propValue: false,
      }, {
        // blacklist this play button setting from undo/redo
        blacklist: true,
      });
    },

    mute() {
      if (this.isSelected) {
        const vid = this.$refs.video;
        vid.muted = !vid.muted;
      }
    },

    setCurrentTime(newTime) {
      this.$refs.video.currentTime = newTime;
    },

    previewTrim(time) {
      if (this.isSelected) {
        this.setCurrentTime(time);
      }
    },
  },
};
</script>
